import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import map from 'lodash/map'
import filter from 'lodash/filter'
import Popup from '@SiteComponents/Popup'
import Button from '@SiteComponents/Button'
import SearchDropdown from '@SiteComponents/SearchDropdown'
import TermbaseSettingsIcon from '@Images/termbase_settings.svg'
import RemoveLanguage from '@Images/remove_termbase_language.svg'

import {
  ITermbase,
  ISaveTermbasePayload,
} from '@SiteModules/Termbase/types'

import '@Root/config/dropdowns.scss'
import './TermbaseSettingsPopup.scss'

interface TermbaseSettingsPopupProps {
  readonly allFullnameLanguages: string[]
  readonly termbaseToSetUp?: ITermbase
  readonly otherTermbasesNames: string[]
  closePopup(): void
  saveTermbase(payload: ISaveTermbasePayload): void
}

const MAX_NAME_LENGTH = 100 // characters
const PROTECTED_NAME = 'None'

const TermbaseSettingsPopup: React.FC<TermbaseSettingsPopupProps> = props => {
  const {
    termbaseToSetUp,
    allFullnameLanguages,
    otherTermbasesNames,
  } = props

  const initialName = termbaseToSetUp ? termbaseToSetUp.attributes.name : ''
  const initialLanguages = termbaseToSetUp ? termbaseToSetUp.attributes.fullnameLanguages : []
  const [name, setName] = useState(initialName)
  const [languages, setLanguages] = useState(initialLanguages)
  const [nameInputIsFocused, setNameInputIsFocused] = useState(false)

  const nameInputIsFocusedRef = useRef(false)
  nameInputIsFocusedRef.current = nameInputIsFocused
  const newName = name.trim()
  const inputUnchanged = newName === initialName && languages === initialLanguages
  const nameTooLong = newName.length > MAX_NAME_LENGTH
  const nameTooShort = newName.length === 0
  const nameAlreadyInUse = otherTermbasesNames.includes(newName)
  const nameProtected = newName === PROTECTED_NAME
  const noLanguagesSelected = languages.length === 0
  const inputCannotBeProcessed = noLanguagesSelected || nameTooShort || nameTooLong || nameAlreadyInUse || nameProtected
  const shouldDisableSaveButton = inputUnchanged || inputCannotBeProcessed

  const [showInfo, setShowInfo] = useState(false)
  const [infoTimeout, setInfoTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)
  const shouldShowInfo = showInfo && inputCannotBeProcessed
  const hasLanguages = languages.length > 0
  const fullnameLanguagesWithoutSelectedLanguages = filter(allFullnameLanguages, fullnameLanguage => !languages.includes(fullnameLanguage))

  let info = ''
  if (noLanguagesSelected) {
    info = 'The languages cannot be empty.'
  } else if (nameTooShort) {
    info = 'The name cannot be empty.'
  } else if (nameTooLong) {
    info = `The name cannot be longer than ${MAX_NAME_LENGTH} characters.`
  } else if (nameAlreadyInUse) {
    info = 'Name alread in use.'
  } else if (nameProtected) {
    info = 'Name cannot be used.'
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown)

    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [])

  const handleSetNameInputIsFocus = () => {
    setNameInputIsFocused(true)
  }

  const handleRemoveNameInputIsFocus = () => {
    setNameInputIsFocused(false)
  }

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName((e.target as HTMLInputElement).value)
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && nameInputIsFocusedRef.current) {
      (document.activeElement as HTMLElement)?.blur()
    }
  }

  const handleSave = () => {
    props.saveTermbase({ name: newName, languages })

    props.closePopup()
  }

  const displayInfo = () => {
    if (infoTimeout) { return }

    const newInfoTimeout = setTimeout(() => {
      setShowInfo(true)
    }, window.Checksub.tooltipDelay)
    setInfoTimeout(newInfoTimeout)
  }

  const hideInfo = () => {
    if (!infoTimeout) { return }

    setShowInfo(false)
    clearTimeout(infoTimeout)
    setInfoTimeout(null)
  }

  const addToLanguages = (newLanguage: string) => {
    if (!languages.includes(newLanguage)) {
      setLanguages(languages.concat(newLanguage))
    }
  }

  const renderNameInput = () => {
    return (
      <div className="TermbaseSettingsPopup__input-wrapper-outer">
        <label className="TermbaseSettingsPopup__input-label">
          Term base name:
        </label>
        <div
          className={classnames(
            'TermbaseSettingsPopup__input-wrapper-inner',
            { 'TermbaseSettingsPopup__input-wrapper-inner-focused': nameInputIsFocused }
          )}
        >
          <input
            value={name}
            onChange={handleChangeName}
            placeholder="Enter a name..."
            onFocus={handleSetNameInputIsFocus}
            onBlur={handleRemoveNameInputIsFocus}
          />
        </div>
      </div>
    )
  }

  const languagesTags = map(languages, (language, index) => {
    const removeFromLanguages = () => {
      setLanguages(filter(languages, lang => lang !== language))
    }

    return (
      <li
        key={index}
        className="TermbaseSettingsPopup__languages-tag"
      >
        <div className="TermbaseSettingsPopup__languages-tag-name">
          {language}
        </div>
        <RemoveLanguage
          onClick={removeFromLanguages}
        />
      </li>
    )
  })

  const renderLanguagesTags = () => {
    return (
      <div className="TermbaseSettingsPopup__languages-tags-wrapper">
        <ul className="TermbaseSettingsPopup__languages-tags">
          {languagesTags}
        </ul>
      </div>
    )
  }

  const renderLanguageSelectionInput = () => {
    return (
      <div className="TermbaseSettingsPopup__input-wrapper-outer">
        <label className="TermbaseSettingsPopup__input-label">
          Languages:
        </label>
        {hasLanguages && renderLanguagesTags()}
        <div id="termbase-settings-popup-input-wrapper-languages">
          <SearchDropdown
            availableOptions={fullnameLanguagesWithoutSelectedLanguages}
            selectedOption=""
            idNameOfParentContainer="termbase-settings-popup-input-wrapper-languages"
            classNameForClickOutsideListener="TermbaseSettingsPopup__input-languages"
            placeholderText="Select a language..."
            setSelectedOption={addToLanguages}
            noOptionFoundText="No language found"
          />
        </div>
      </div>
    )
  }

  return (
    <Popup
      className="TermbaseSettingsPopup"
      closePopup={props.closePopup}
    >
      <div className="TermbaseSettingsPopup__header">
        <TermbaseSettingsIcon />
        Term Base Settings
      </div>
      {renderNameInput()}
      {renderLanguageSelectionInput()}
      <div className="TermbaseSettingsPopup__button-wrapper">
        <Button
          styleType="gray-subtle"
          size="small"
          caption="Cancel"
          onClick={props.closePopup}
        />
        <div
          onMouseEnter={displayInfo}
          onMouseLeave={hideInfo}
        >
          <Button
            styleType="brand"
            size="small"
            caption="Save"
            onClick={handleSave}
            disabled={shouldDisableSaveButton}
          />
        </div>
        {shouldShowInfo && (
          <div className="TermbaseSettingsPopup__button-tooltip">
            {info}
          </div>
        )}
      </div>
    </Popup>
  )
}

export default TermbaseSettingsPopup
